import { useEffect, useState, useRef, useCallback } from 'react';
import { apiGet, apiPatch, apiPost, apiDelete, ApiClientError } from '@client/lib/api';
import { formatPhone } from '@shared/phone';
import { useSession } from '@client/lib/useSession';
import styles from './SettingsPage.module.css';

/** The organization settings as returned by GET /api/v1/organization. */
interface Organization {
  id: string;
  name: string;
  slug: string;
  phone: string | null;
  email: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  timezone: string;
  defaultTaxRateBps: number;
  defaultInvoiceTerms: string | null;
  nextInvoiceNumber: number;
  nextQuoteNumber: number;
}

/** The server response after PATCHing the organization: the full updated row. */
type UpdatedOrganization = Organization;

/** US timezones offered in the UI. A typo should not be possible. */
const US_TIMEZONES = [
  'America/Chicago',
  'America/New_York',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
] as const;

/** Minimum password length enforced by the server. */
const MIN_PASSWORD_LENGTH = 12;

/**
 * Compute the current GMT offset for an IANA timezone, e.g. "GMT-5" or
 * "GMT+5:30". Uses the platform Intl API against the ACTUAL current date so
 * that daylight saving time is respected. Falls back to the bare IANA name
 * if the platform cannot produce an offset (so the settings screen always
 * renders).
 *
 * Whole-hour offsets are normalised to "GMT-5" (dropping the ":00").
 */
function timezoneOffsetLabel(timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    const tzPart = parts.find((p) => p.type === 'timeZoneName');
    if (!tzPart || !tzPart.value) return timeZone;
    // Intl yields strings like "GMT-5", "GMT+5", "GMT-5:30", "GMT-5:00".
    const raw = tzPart.value;
    // Normalise "GMT-5:00" -> "GMT-5" (drop a trailing ":00").
    const normalised = raw.replace(/:00$/, '');
    return `${timeZone} (${normalised})`;
  } catch {
    return timeZone;
  }
}

type ViewState =
  { kind: 'loading' } | { kind: 'error'; message: string } | { kind: 'loaded'; org: Organization };

/** Convert stored basis points to a display percentage string. */
function bpsToDisplay(bps: number): string {
  return (bps / 100).toString();
}

/** Convert a display percentage string to basis points. Returns NaN if invalid. */
function displayToBps(value: string): number {
  const pct = Number(value);
  if (Number.isNaN(pct)) return NaN;
  return Math.round(pct * 100);
}

/** A team member as returned by GET /api/v1/team. */
interface TeamMember {
  id: string;
  email: string;
  displayName: string;
  role: 'OWNER' | 'CREW';
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

/** An audit event as returned by GET /api/v1/audit. */
interface AuditEvent {
  id: string;
  createdAt: string;
  action: string;
  entityType: string;
  entityId: string | null;
  userId: string | null;
  userDisplayName: string | null;
  metadata: Record<string, unknown> | null;
}

/** Audit response with cursor-based pagination. */
interface AuditResponse {
  events: AuditEvent[];
  nextCursor: string | null;
}

/** Human-readable labels for audit actions. Unknown actions fall back to a
 *  prettified version of the raw string. */
const ACTION_LABELS: Record<string, string> = {
  team_member_added: 'Team member added',
  team_member_removed: 'Team member deactivated',
  team_member_reactivated: 'Team member reactivated',
  team_member_role_changed: 'Team member role changed',
  organization_updated: 'Organization updated',
  invoice_created: 'Invoice created',
  invoice_updated: 'Invoice updated',
  invoice_deleted: 'Invoice deleted',
  customer_created: 'Customer created',
  customer_updated: 'Customer updated',
  customer_deleted: 'Customer deleted',
  job_created: 'Job created',
  job_updated: 'Job updated',
  job_deleted: 'Job deleted',
  service_created: 'Service created',
  service_updated: 'Service updated',
  service_deleted: 'Service deleted',
  payment_received: 'Payment received',
  payment_refunded: 'Payment refunded',
};

/** Human-readable labels for entity types. */
const ENTITY_LABELS: Record<string, string> = {
  organization: 'Organization',
  team_member: 'Team member',
  invoice: 'Invoice',
  customer: 'Customer',
  job: 'Job',
  service: 'Service',
  payment: 'Payment',
  quote: 'Quote',
  recurring: 'Recurring job',
};

/**
 * Prettify an unknown action/entity string: snake_case -> "Snake case".
 * Never throws.
 */
function prettify(raw: string): string {
  // Sentence case, not Title Case: /\b\w/g capitalises EVERY word, turning
  // 'some_future_action' into 'Some Future Action', which reads like a proper
  // noun sitting next to hand-written labels that are all sentence case.
  const spaced = raw.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Lookup a label, falling back to a prettified version of the raw string. */
function labelFor(raw: string, map: Record<string, string>): string {
  return map[raw] ?? prettify(raw);
}

/**
 * The Settings screen.
 *
 * GET /api/v1/organization for the current settings, PATCH for updates, and
 * POST /api/v1/organization/password for a password change.
 *
 * No optimistic updates: a field keeps its previous value until the server
 * confirms. Controls disable and show pending while saving; on success the
 * screen re-renders from the server's response; on failure the previous value
 * is restored and the server's message is shown.
 */
export function SettingsPage() {
  const { user } = useSession();
  const isOwner = user?.role === 'OWNER';

  const [state, setState] = useState<ViewState>({ kind: 'loading' });

  /** id of the field currently being saved, or null. */
  const [savingField, setSavingField] = useState<string | null>(null);
  /** error message for a failed save, or null. */
  const [saveError, setSaveError] = useState<string | null>(null);

  /** draft tax display value (percentage string). */
  const [taxDraft, setTaxDraft] = useState<string>('');

  /** draft password values. */
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  /** whether the password change is in flight. */
  const [passwordSaving, setPasswordSaving] = useState(false);
  /** error message for a failed password change, or null. */
  const [passwordError, setPasswordError] = useState<string | null>(null);
  /** whether the password change succeeded. */
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  /** ---- Team section state ---- */
  const [teamMembers, setTeamMembers] = useState<TeamMember[] | null>(null);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [teamSaving, setTeamSaving] = useState(false);
  const [teamSaveError, setTeamSaveError] = useState<string | null>(null);
  /** draft values for the add-member form. */
  const [addMemberForm, setAddMemberForm] = useState({
    displayName: '',
    email: '',
  });
  /** ref guard against double-submit of add-member. */
  const addMemberSubmittingRef = useRef(false);
  /** ref guard against double-submit of deactivate/reactivate. */
  const deactivateSubmittingRef = useRef<Record<string, boolean>>(Object.create(null));
  /**
   * The invitation link for one member, shown until it is dismissed.
   *
   * Held in state and NOT re-fetchable, because the server only ever returns a
   * token once — it stores a hash. Navigating away loses it, and the remedy is
   * to mint a new one, which is exactly what the Invite button does.
   */
  const [inviteLink, setInviteLink] = useState<{
    memberId: string;
    displayName: string;
    url: string;
  } | null>(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  /** ref guard against double-submit of invite, per member. */
  const inviteSubmittingRef = useRef<Record<string, boolean>>(Object.create(null));

  /** ---- Activity section state ---- */
  const [auditEvents, setAuditEvents] = useState<AuditEvent[] | null>(null);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [auditNextCursor, setAuditNextCursor] = useState<string | null>(null);
  const [auditLoadingMore, setAuditLoadingMore] = useState(false);
  const [auditLoadMoreError, setAuditLoadMoreError] = useState<string | null>(null);

  /**
   * The committed organization — the value from the last server response.
   * This is the "old value" that fields revert to on blur / failure.
   * We keep it in a ref so that blur handlers always see the committed value
   * even after the user has typed into the field (which updates `state`).
   */
  const committedOrgRef = useRef<Organization | null>(null);

  const fetchOrg = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const org = await apiGet<Organization>('/api/v1/organization');
      committedOrgRef.current = org;
      setTaxDraft(bpsToDisplay(org.defaultTaxRateBps));
      setState({ kind: 'loaded', org });
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof ApiClientError ? err.message : 'Could not load your settings.',
      });
    }
  }, []);

  /** Fetch the team roster. Independent of the org fetch. */
  const fetchTeam = useCallback(async () => {
    setTeamError(null);
    try {
      const members = await apiGet<TeamMember[]>('/api/v1/team');
      setTeamMembers(members ?? []);
    } catch (err) {
      setTeamError(err instanceof ApiClientError ? err.message : 'Could not load the team roster.');
    }
  }, []);

  /** Fetch the first page of audit events. Independent of the team fetch. */
  const fetchAudit = useCallback(async () => {
    setAuditError(null);
    try {
      const resp = await apiGet<AuditResponse>('/api/v1/audit');
      setAuditEvents(resp.events);
      setAuditNextCursor(resp.nextCursor);
    } catch (err) {
      setAuditError(err instanceof ApiClientError ? err.message : 'Could not load activity.');
    }
  }, []);

  /** Load more audit events, appending to the existing list. */
  const loadMoreAudit = useCallback(async () => {
    if (!auditNextCursor) return;
    setAuditLoadMoreError(null);
    setAuditLoadingMore(true);
    try {
      const resp = await apiGet<AuditResponse>(
        `/api/v1/audit?cursor=${encodeURIComponent(auditNextCursor)}`
      );
      setAuditEvents((prev) => {
        const existing = prev ?? [];
        return [...existing, ...resp.events];
      });
      setAuditNextCursor(resp.nextCursor);
    } catch (err) {
      setAuditLoadMoreError(
        err instanceof ApiClientError ? err.message : 'Could not load more activity.'
      );
    } finally {
      setAuditLoadingMore(false);
    }
  }, [auditNextCursor]);

  useEffect(() => {
    void fetchOrg();
  }, [fetchOrg]);

  /** Fetch team and audit on mount. These are independent of the org fetch
   *  and of each other — a failure in one must not blank the other. */
  useEffect(() => {
    void fetchTeam();
  }, [fetchTeam]);

  useEffect(() => {
    void fetchAudit();
  }, [fetchAudit]);

  /**
   * Save a single business/preference field.
   *
   * NO OPTIMISTIC UPDATE: revert the displayed value to the committed value
   * immediately, before the request resolves. The input is disabled while the
   * save is in flight. Only the server's response will update the displayed
   * value.
   */
  const saveField = useCallback(async (field: keyof Organization, newValue: unknown) => {
    const committed = committedOrgRef.current;
    if (!committed) return;
    const oldValue = committed[field];

    // Revert the displayed value to the committed value immediately.
    setState((prev) => {
      if (prev.kind !== 'loaded') return prev;
      return { kind: 'loaded', org: { ...prev.org, [field]: oldValue } };
    });
    setSavingField(field);
    setSaveError(null);
    try {
      const updated = await apiPatch<UpdatedOrganization>('/api/v1/organization', {
        [field]: newValue,
      });
      // Re-render from the SERVER's response.
      committedOrgRef.current = updated;
      if (field === 'defaultTaxRateBps') {
        setTaxDraft(bpsToDisplay(updated.defaultTaxRateBps));
      }
      setState({ kind: 'loaded', org: updated });
    } catch (err) {
      // On failure: the committed value is already shown. Surface the
      // server's message.
      if (field === 'defaultTaxRateBps') {
        setTaxDraft(bpsToDisplay(committed.defaultTaxRateBps));
      }
      setSaveError(
        err instanceof ApiClientError
          ? err.message
          : 'Could not save this change. Please try again.'
      );
    } finally {
      setSavingField(null);
    }
  }, []);

  const handleFieldChange =
    (field: keyof Organization) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      if (state.kind !== 'loaded') return;
      setState({
        kind: 'loaded',
        org: { ...state.org, [field]: e.target.value },
      });
    };

  const handleFieldBlur = useCallback(
    (field: keyof Organization) => () => {
      if (state.kind !== 'loaded') return;
      void saveField(field, state.org[field]);
    },
    [state, saveField]
  );

  /**
   * Canonicalise the business's phone number as the field is left, then save.
   *
   * Same rule as the customer forms — one format in the database, so the number
   * printed on a quote looks the way every other number in the app does. On
   * blur rather than per keystroke, for the same reason: reformatting while
   * someone is typing moves the caret out from under them.
   *
   * An unrecognisable number is saved as typed and refused by the server, which
   * is what surfaces the message. Silently discarding it here would be worse.
   */
  const handlePhoneBlur = useCallback(() => {
    if (state.kind !== 'loaded') return;
    const typed = state.org.phone ?? '';
    const formatted = formatPhone(typed) ?? typed;
    if (formatted !== typed) {
      setState((prev) =>
        prev.kind === 'loaded' ? { ...prev, org: { ...prev.org, phone: formatted } } : prev
      );
    }
    void saveField('phone', formatted);
  }, [state, saveField]);

  const handleTaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTaxDraft(e.target.value);
  };

  const handleTaxBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (state.kind !== 'loaded') return;
      const committed = committedOrgRef.current;
      if (!committed) return;
      const oldBps = committed.defaultTaxRateBps;
      const displayValue = e.target.value;
      const pct = Number(displayValue);
      // Reject input that is not a number, is negative, or exceeds 100.
      if (Number.isNaN(pct) || pct < 0 || pct > 100) {
        // Revert to the committed value without sending a request.
        setTaxDraft(bpsToDisplay(oldBps));
        setSaveError('Tax rate must be a number between 0 and 100.');
        return;
      }
      const bps = displayToBps(displayValue);
      void saveField('defaultTaxRateBps', bps);
    },
    [state, saveField]
  );

  const handlePasswordChange =
    (field: keyof typeof passwordForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setPasswordForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handlePasswordSubmit = useCallback(async () => {
    const { currentPassword, newPassword, confirmNewPassword } = passwordForm;

    // Mismatched confirm: show an error and send NO request.
    if (newPassword !== confirmNewPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }

    // Under 12 characters: show an error and send NO request.
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setPasswordError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    setPasswordSaving(true);
    setPasswordError(null);
    setPasswordSuccess(false);
    try {
      await apiPost('/api/v1/organization/password', {
        currentPassword,
        newPassword,
      });
      // On success: clear the fields and tell the owner their other devices
      // have been signed out, because that is what the server does.
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
      });
      setPasswordSuccess(true);
    } catch (err) {
      // A 401 (wrong current password) comes back with the server's message.
      // Do not clear the fields on failure.
      setPasswordError(
        err instanceof ApiClientError
          ? err.message
          : 'Could not change your password. Please try again.'
      );
    } finally {
      setPasswordSaving(false);
    }
  }, [passwordForm]);

  /** ---- Team: add member ---- */
  const handleAddMemberChange =
    (field: keyof typeof addMemberForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setAddMemberForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleAddMemberSubmit = useCallback(async () => {
    // REF guard against double-submit: two taps in one tick both run before
    // React re-renders.
    if (addMemberSubmittingRef.current) return;
    addMemberSubmittingRef.current = true;
    setTeamSaving(true);
    setTeamSaveError(null);
    try {
      const created = await apiPost<TeamMember & { invite?: { url: string } }>('/api/v1/team', {
        email: addMemberForm.email,
        displayName: addMemberForm.displayName,
        role: 'CREW',
      });
      // Surface the link immediately. A member added without one cannot sign
      // in at all, so hiding it behind a second click would routinely leave
      // somebody stranded.
      if (created.invite?.url) {
        setInviteCopied(false);
        setInviteLink({
          memberId: created.id,
          displayName: created.displayName,
          url: created.invite.url,
        });
      }
      // On success: clear the form and refresh the roster.
      setAddMemberForm({ displayName: '', email: '' });
      void fetchTeam();
    } catch (err) {
      // A 409 (email exists) or 400 (bad role) keeps the form filled in so the
      // owner can correct it. Surface the server's message.
      setTeamSaveError(
        err instanceof ApiClientError ? err.message : 'Could not add this member. Please try again.'
      );
    } finally {
      setTeamSaving(false);
      addMemberSubmittingRef.current = false;
    }
  }, [addMemberForm, fetchTeam]);

  /** ---- Team: mint an invitation link ---- */
  const handleInviteMember = useCallback(async (member: TeamMember) => {
    if (inviteSubmittingRef.current[member.id]) return;
    inviteSubmittingRef.current[member.id] = true;
    setTeamSaving(true);
    setTeamSaveError(null);
    try {
      // A deactivated member has no usable sign-in, so the server would reject
      // or strand an invite; reactivate first. The PATCH also deletes their
      // (unusable) sessions and writes an audit row; both are expected.
      if (!member.isActive) {
        await apiPatch<TeamMember>(`/api/v1/team/${member.id}`, { isActive: true });
      }
      const result = await apiPost<{ invite: { url: string } }>(`/api/v1/team/${member.id}/invite`);
      setInviteCopied(false);
      setInviteLink({
        memberId: member.id,
        displayName: member.displayName,
        url: result.invite.url,
      });
    } catch (err) {
      setTeamSaveError(
        err instanceof ApiClientError
          ? err.message
          : 'Could not create an invitation link. Please try again.'
      );
    } finally {
      setTeamSaving(false);
      inviteSubmittingRef.current[member.id] = false;
    }
  }, []);

  const handleCopyInvite = useCallback(async () => {
    if (!inviteLink) return;
    try {
      // Not available over plain http or in older browsers. The link is also
      // rendered in a selectable field, which is why a failure here is a
      // non-event rather than an error worth showing.
      await navigator.clipboard?.writeText(inviteLink.url);
      setInviteCopied(true);
    } catch {
      setInviteCopied(false);
    }
  }, [inviteLink]);

  /** ---- Team: deactivate / reactivate ---- */
  const handleToggleMember = useCallback(
    async (member: TeamMember) => {
      // REF guard against double-submit per member.
      if (deactivateSubmittingRef.current[member.id]) return;
      deactivateSubmittingRef.current[member.id] = true;
      setTeamSaving(true);
      setTeamSaveError(null);
      try {
        if (member.isActive) {
          // Deactivate: DELETE /api/v1/team/:id (server deactivates).
          await apiDelete(`/api/v1/team/${member.id}`);
        } else {
          // Reactivate: PATCH { isActive: true }.
          await apiPatch<TeamMember>(`/api/v1/team/${member.id}`, {
            isActive: true,
          });
        }
        void fetchTeam();
      } catch (err) {
        // A 400 (last active owner) surfaces the server's message.
        setTeamSaveError(
          err instanceof ApiClientError
            ? err.message
            : 'Could not update this member. Please try again.'
        );
      } finally {
        setTeamSaving(false);
        delete deactivateSubmittingRef.current[member.id];
      }
    },
    [fetchTeam]
  );

  if (state.kind === 'loading') {
    return (
      <main id="main" className={styles.container}>
        <p className={styles.status} aria-live="polite">
          Loading settings...
        </p>
      </main>
    );
  }

  if (state.kind === 'error') {
    return (
      <main id="main" className={styles.container}>
        <p className={styles.status} role="alert">
          {state.message}
        </p>
        <button type="button" className={styles.retryButton} onClick={() => void fetchOrg()}>
          Retry
        </button>
      </main>
    );
  }

  const { org } = state;

  return (
    <main id="main" className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Settings</h1>
      </header>

      {/* A) Business details */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Business details</h2>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Name</span>
          <input
            type="text"
            className={styles.input}
            value={org.name ?? ''}
            onChange={handleFieldChange('name')}
            onBlur={handleFieldBlur('name')}
            disabled={savingField === 'name'}
            aria-busy={savingField === 'name'}
          />
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Phone</span>
          <input
            type="tel"
            className={styles.input}
            value={org.phone ?? ''}
            onChange={handleFieldChange('phone')}
            onBlur={handlePhoneBlur}
            disabled={savingField === 'phone'}
            aria-busy={savingField === 'phone'}
            aria-label="Business phone"
          />
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Email</span>
          <input
            type="email"
            className={styles.input}
            value={org.email ?? ''}
            onChange={handleFieldChange('email')}
            onBlur={handleFieldBlur('email')}
            disabled={savingField === 'email'}
            aria-busy={savingField === 'email'}
          />
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Address line 1</span>
          <input
            type="text"
            className={styles.input}
            value={org.addressLine1 ?? ''}
            onChange={handleFieldChange('addressLine1')}
            onBlur={handleFieldBlur('addressLine1')}
            disabled={savingField === 'addressLine1'}
            aria-busy={savingField === 'addressLine1'}
          />
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Address line 2</span>
          <input
            type="text"
            className={styles.input}
            value={org.addressLine2 ?? ''}
            onChange={handleFieldChange('addressLine2')}
            onBlur={handleFieldBlur('addressLine2')}
            disabled={savingField === 'addressLine2'}
            aria-busy={savingField === 'addressLine2'}
          />
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>City</span>
          <input
            type="text"
            className={styles.input}
            value={org.city ?? ''}
            onChange={handleFieldChange('city')}
            onBlur={handleFieldBlur('city')}
            disabled={savingField === 'city'}
            aria-busy={savingField === 'city'}
          />
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>State</span>
          <input
            type="text"
            className={styles.input}
            value={org.state ?? ''}
            onChange={handleFieldChange('state')}
            onBlur={handleFieldBlur('state')}
            disabled={savingField === 'state'}
            aria-busy={savingField === 'state'}
          />
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Postal code</span>
          <input
            type="text"
            className={styles.input}
            value={org.postalCode ?? ''}
            onChange={handleFieldChange('postalCode')}
            onBlur={handleFieldBlur('postalCode')}
            disabled={savingField === 'postalCode'}
            aria-busy={savingField === 'postalCode'}
          />
        </div>
        {saveError && (
          <p className={styles.fieldError} role="alert">
            {saveError}
          </p>
        )}
      </section>

      {/* B) Preferences */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Preferences</h2>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Timezone</span>
          <select
            className={styles.input}
            value={org.timezone ?? ''}
            onChange={handleFieldChange('timezone')}
            onBlur={handleFieldBlur('timezone')}
            disabled={savingField === 'timezone'}
            aria-busy={savingField === 'timezone'}
            aria-label="Timezone"
          >
            {US_TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {timezoneOffsetLabel(tz)}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Default invoice terms</span>
          <input
            type="text"
            className={styles.input}
            value={org.defaultInvoiceTerms ?? ''}
            onChange={handleFieldChange('defaultInvoiceTerms')}
            onBlur={handleFieldBlur('defaultInvoiceTerms')}
            disabled={savingField === 'defaultInvoiceTerms'}
            aria-busy={savingField === 'defaultInvoiceTerms'}
          />
        </div>
        {saveError && (
          <p className={styles.fieldError} role="alert">
            {saveError}
          </p>
        )}
      </section>

      {/* C) Tax */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Tax</h2>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Default sales tax rate (%)</span>
          <input
            type="text"
            className={styles.input}
            value={taxDraft}
            onChange={handleTaxChange}
            onBlur={handleTaxBlur}
            disabled={savingField === 'defaultTaxRateBps'}
            aria-busy={savingField === 'defaultTaxRateBps'}
          />
        </div>
        {saveError && (
          <p className={styles.fieldError} role="alert">
            {saveError}
          </p>
        )}
      </section>

      {/* D) Password */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Password</h2>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Current password</span>
          <input
            type="password"
            className={styles.input}
            value={passwordForm.currentPassword}
            onChange={handlePasswordChange('currentPassword')}
            disabled={passwordSaving}
            aria-busy={passwordSaving}
            aria-label="Current password"
          />
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>New password</span>
          <input
            type="password"
            className={styles.input}
            value={passwordForm.newPassword}
            onChange={handlePasswordChange('newPassword')}
            disabled={passwordSaving}
            aria-busy={passwordSaving}
            aria-label="New password"
          />
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Confirm new password</span>
          <input
            type="password"
            className={styles.input}
            value={passwordForm.confirmNewPassword}
            onChange={handlePasswordChange('confirmNewPassword')}
            disabled={passwordSaving}
            aria-busy={passwordSaving}
            aria-label="Confirm new password"
          />
        </div>
        {passwordError && (
          <p className={styles.fieldError} role="alert">
            {passwordError}
          </p>
        )}
        {passwordSuccess && (
          <p className={styles.success} role="status">
            Your password has been changed. You have been signed out on your other devices.
          </p>
        )}
        <button
          type="button"
          className={styles.saveButton}
          disabled={passwordSaving}
          aria-busy={passwordSaving}
          onClick={() => void handlePasswordSubmit()}
        >
          {passwordSaving ? 'Saving...' : 'Change password'}
        </button>
      </section>

      {/* E) Team — owner only */}
      {isOwner && (
        <section className={styles.section} aria-labelledby="team-heading">
          <h2 id="team-heading" className={styles.sectionTitle}>
            Team
          </h2>

          {teamError ? (
            <p className={styles.fieldError} role="alert">
              {teamError}
            </p>
          ) : teamMembers === null ? (
            <p className={styles.status} aria-live="polite">
              Loading team...
            </p>
          ) : (
            <>
              <table className={styles.teamTable} aria-labelledby="team-heading">
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Email</th>
                    <th scope="col">Role</th>
                    <th scope="col">Status</th>
                    <th scope="col" aria-label="Actions">
                      <span aria-hidden="true"> </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {teamMembers.map((member) => {
                    const isSelf = user?.id === member.id;
                    const isDeactivated = !member.isActive;
                    return (
                      <tr
                        key={member.id}
                        className={isDeactivated ? styles.teamRowDeactivated : undefined}
                      >
                        <td className={styles.teamMemberName}>{member.displayName}</td>
                        <td>{member.email}</td>
                        <td>{member.role}</td>
                        <td>
                          {isDeactivated ? (
                            <span className={styles.deactivatedBadge}>Deactivated</span>
                          ) : (
                            'Active'
                          )}
                        </td>
                        <td>
                          {!isSelf && (
                            <>
                              <button
                                type="button"
                                className={styles.teamActionBtn}
                                disabled={teamSaving}
                                aria-busy={teamSaving}
                                onClick={() => void handleInviteMember(member)}
                              >
                                Invite link
                              </button>
                              <button
                                type="button"
                                className={
                                  member.isActive
                                    ? styles.teamActionBtnDanger
                                    : styles.teamActionBtn
                                }
                                disabled={teamSaving}
                                aria-busy={teamSaving}
                                onClick={() => void handleToggleMember(member)}
                              >
                                {member.isActive ? 'Deactivate' : 'Reactivate'}
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {inviteLink && (
                <div className={styles.inviteBox} role="status" aria-live="polite">
                  <p className={styles.inviteHeading}>
                    Invitation link for {inviteLink.displayName}
                  </p>
                  <p className={styles.inviteHelp}>
                    Send this to them. It works once, expires in 7 days, and lets them choose their
                    own password. Inviting a deactivated member reactivates them first. It is shown
                    here only now — make a new one any time.
                  </p>
                  {/*
                    A readonly input rather than text: it is selectable, it
                    scrolls rather than wrapping a 100-character URL across the
                    card, and it works when the clipboard API does not.
                  */}
                  <input
                    type="text"
                    className={styles.inviteUrl}
                    value={inviteLink.url}
                    readOnly
                    aria-label="Invitation link"
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <div className={styles.inviteActions}>
                    <button
                      type="button"
                      className={styles.teamActionBtn}
                      onClick={() => void handleCopyInvite()}
                    >
                      {inviteCopied ? 'Copied' : 'Copy link'}
                    </button>
                    <button
                      type="button"
                      className={styles.teamActionBtn}
                      onClick={() => setInviteLink(null)}
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}

              <div className={styles.field}>
                <span className={styles.fieldLabel}>Add a helper</span>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="Display name"
                  value={addMemberForm.displayName}
                  onChange={handleAddMemberChange('displayName')}
                  disabled={teamSaving}
                  aria-label="Display name"
                />
                <input
                  type="email"
                  className={styles.input}
                  placeholder="Email address"
                  value={addMemberForm.email}
                  onChange={handleAddMemberChange('email')}
                  disabled={teamSaving}
                  aria-label="Email address"
                />
                {/*
                  Keep this in step with what the server actually does. It used
                  to read "there is no password to send", which was true only
                  while an added member had no way to obtain one — the exact
                  limitation invitations removed. A member reading the stale
                  version would be sent to Google while holding a link that sets
                  a password. tests/client/SettingsPage.test.tsx pins it.
                */}
                <p className={styles.teamFormNote}>
                  Everyone added here is a Crew member (role: CREW). You will get a one-time
                  invitation link to send them; it expires in 7 days and lets them choose their own
                  password. Google sign-in works too, if they have a Google account on that address.
                </p>
                {teamSaveError && (
                  <p className={styles.fieldError} role="alert">
                    {teamSaveError}
                  </p>
                )}
                <button
                  type="button"
                  className={styles.saveButton}
                  disabled={teamSaving}
                  aria-busy={teamSaving}
                  onClick={() => void handleAddMemberSubmit()}
                >
                  {teamSaving ? 'Adding...' : 'Add member'}
                </button>
              </div>
            </>
          )}
        </section>
      )}

      {/* F) Activity — owner only */}
      {isOwner && (
        <section className={styles.section} aria-labelledby="activity-heading">
          <h2 id="activity-heading" className={styles.sectionTitle}>
            Activity
          </h2>

          {auditError ? (
            <p className={styles.fieldError} role="alert">
              {auditError}
            </p>
          ) : auditEvents === null ? (
            <p className={styles.status} aria-live="polite">
              Loading activity...
            </p>
          ) : auditEvents.length === 0 ? (
            <p className={styles.emptyState}>There is no activity yet.</p>
          ) : (
            <>
              <table className={styles.activityTable} aria-labelledby="activity-heading">
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">Who</th>
                    <th scope="col">What</th>
                  </tr>
                </thead>
                <tbody>
                  {auditEvents.map((event) => (
                    <tr key={event.id}>
                      <td>{new Date(event.createdAt).toLocaleString()}</td>
                      <td>{event.userDisplayName ?? 'a removed user'}</td>
                      {/*
                        Action and entity are separate elements rather than one
                        fused string: a single text node reading "Added a member
                        on user" cannot be matched by either half, which makes
                        the label mapping effectively untestable.
                      */}
                      <td>
                        <span>{labelFor(event.action, ACTION_LABELS)}</span>
                        {' on '}
                        <span>{labelFor(event.entityType, ENTITY_LABELS)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {auditLoadMoreError && (
                <p className={styles.fieldError} role="alert">
                  {auditLoadMoreError}
                </p>
              )}

              {auditNextCursor !== null && (
                <button
                  type="button"
                  className={styles.loadMoreBtn}
                  disabled={auditLoadingMore}
                  aria-busy={auditLoadingMore}
                  onClick={() => void loadMoreAudit()}
                >
                  {auditLoadingMore ? 'Loading...' : 'Load more'}
                </button>
              )}
            </>
          )}
        </section>
      )}
    </main>
  );
}
